import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Modal, LinearProgress, Button } from '@material-ui/core';
import styles from './LineTransferSelectorDisplay.module.css';
import { ASN_STATUS, DATE_FORMAT, AGGREGATE_STATUS } from '../../constants';
import { getDatesForRange } from '../../utils/utils';
import '../../cssModules/switch.css';
import TodayIcon from '@material-ui/icons/Today';
import AttributesSelector from '../AttributesSelector/AttributesSelector';
import { optionalColumnsWithShift } from '../../staticData';
import { getSelectedOptionalColumns } from '../../utils/tableUtils';
import VirtualizedDragAndDropList from '../VirtualizedDragAndDropList/VirtualizedDragAndDropList';

const LineTransferSelectorDisplay = ({
  asnsSelectedForLineTransfer, // Now an array of ASNs
  handleClose,
  open,
  updateRows,
  asnData,
  asnSelectorheaderText,
  setAsnData,
  lastAsnOrAgsnGeneratedDate,
  handleLineOnSubmit,
  setActiveDate,
  activeDate,
  fixedColumns,
  selectedLine,
}) => {
  const [asnDates, setAsnDates] = useState([]);
  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);
  const [indexOfLastAnsNotInPlannedState, setIndexOfLastAnsNotInPlannedState] = useState(-1);
  const [isLoadingAsnToLineOn, setIsLoadingAsnToLineOn] = useState(true);

  // Insert multiple ASNs before the first planned ASN
  // Insert multiple ASNs at the bottom of the list
const updateLineSequenceWithGivenAsns = (asns) => {
    if (!asnsSelectedForLineTransfer || asnsSelectedForLineTransfer.length === 0) {
      console.warn("No ASNs to line Transfer provided.");
      return;
    }
    const asnsSelectedForLineTransferIds = new Set(asnsSelectedForLineTransfer.map((asn) => asn.asnId || asn.agsnId));
    const rows = asns.filter((res) => !asnsSelectedForLineTransferIds.has(res.asnId || res.agsnId));
  
    // Append all ASNs to the bottom of the list
    const updatedSequenceWithLineTransferedAsns = [
      ...rows,
      ...asnsSelectedForLineTransfer.map((asn) => ({ ...asn, isToBeLinedTranfer: true })),
    ];
  
    setAsnData((prevState) => ({ ...prevState, data: updatedSequenceWithLineTransferedAsns }));
  };

  // Load the index of the last ASN not in planned state
  const getIndexOfLastAsnNotInPlannedState = (asns) => {
    const asnsNotInPlanningState = asns.filter(
      (asn) =>
        asn.status !== ASN_STATUS.PLANNED && asn.status !== AGGREGATE_STATUS.ASN_GENERATED
    );
    const lastAsnNotInPlannedState = asnsNotInPlanningState[asnsNotInPlanningState.length - 1];
    setIndexOfLastAnsNotInPlannedState(asns.indexOf(lastAsnNotInPlannedState));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    if (
      result.source.index === result.destination.index ||
      result.destination.index <= indexOfLastAnsNotInPlannedState
    ) {
      return;
    }
    const rows = asnData.data;
    const [removed] = rows.splice(result.source.index, 1);
    rows.splice(result.destination.index, 0, removed);
    setAsnData((prevState) => ({ ...prevState, data: rows }));
  };

  // Render the table view
  const renderTableView = () => {
    return (
      <>
        <div className={styles.header}>
          <div className={styles.lineName}>{selectedLine}</div>
          <div className={styles.title}>
            <span style={{ display: "block" }}>Drop Sequence</span>
            <span style={{ display: "block", fontWeight: "600" }}>
              Select Slot
            </span>
          </div>
          <div className={styles.tableTop}>
            <div className={styles.calicon}>
              <span>For</span>
              <TodayIcon />
            </div>
            <div className={styles.datesSelectorBar}>
              <span className={styles.monthName}>
                {activeDate ? activeDate.format("MMMM") : ''}
              </span>
              <div className={styles.hrScroll}>
                <div className={styles.datesRow}>
                  {asnDates.map((date) => (
                    <button
                      className={
                        date.isSame(activeDate, 'day')
                          ? styles.activeDateButton
                          : styles.datesSelectorButton
                      }
                      onClick={() => setActiveDate(date)}
                      key={date.format(DATE_FORMAT.ISO)}
                    >
                      {date.format("DD")}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <span className={styles.dragAndDropNote}>
              {asnSelectorheaderText}
            </span>
            <div className={styles.ToolBar}>
              <AttributesSelector
                className={styles.Attribute}
                columns={optionalColumnsWithShift}
                selectedOptionalCols={selectedOptionalColNames}
                applyColumnsSelection={(selection) =>
                  setSelectedOptionalColNames(selection)
                }
                isShiftPresent={true}
              />
            </div>
          </div>
        </div>

        <div className={styles.mainTableContainer} style={{ height: 400 }}>
          <div className={styles.tableContainer}>
            <>
              <div className={styles.tableHeight} style={{ height: 370 }}>
                <VirtualizedDragAndDropList
                  columns={[
                    ...fixedColumns.map((column) => ({
                      ...column,
                      headerStyles: {
                        color: "#0275A7",
                        fontSize: "13px",
                        fontWeight: "600",
                        boxSizing: "border-box",
                      },
                    })),
                    ...getSelectedOptionalColumns(
                      optionalColumnsWithShift,
                      selectedOptionalColNames,
                      {
                        headerStyles: {
                          color: "#707070",
                          backgroundColor: "white",
                          zIndex: 1,
                          boxSizing: "border-box",
                        },
                      }
                    ),
                  ]}
                  rows={asnData.data || []}
                  listHeight={370}
                  rowHeight={40}
                  classNames={{
                    tableRowClassName: (row) => {
                      if (row.isToBeLinedTranfer) return styles.lineOnRow;
                      if (
                        row.status !== ASN_STATUS.PLANNED &&
                        row.status !== AGGREGATE_STATUS.ASN_GENERATED
                      )
                        return styles.greyedOutRow;
                      return styles.defautRow;
                    },
                  }}
                  shouldDisableDragForRow={(row) => !row.isToBeLinedTranfer}
                  shouldFixRow={(row) =>
                    row.status !== ASN_STATUS.PLANNED &&
                    row.status !== ASN_STATUS.LINE_OUT &&
                    row.status !== AGGREGATE_STATUS.ASN_GENERATED &&
                    row.status !== AGGREGATE_STATUS.LINED_OUT
                  }
                  onDragEnd={handleDragEnd}
                />
              </div>
            </>
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            data-testid="asn-select-back"
            className={`${styles.footerButton} ${styles.backButton}`} // Apply both classes
            variant="contained"
            onClick={handleClose}
          >
            Back
          </Button>
          <Button
            data-testid="asn-select-confirm"
            className={styles.footerButton}
            variant="contained"
            onClick={() => {
              handleLineOnSubmit(asnData, activeDate); // Pass activeDate to handleLineOnSubmit
            }}
          >
            Confirm
          </Button>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (lastAsnOrAgsnGeneratedDate) {
      const dates = getDatesForRange(moment(), lastAsnOrAgsnGeneratedDate);
      setAsnDates(dates);
      if (!activeDate) {
        setActiveDate(dates[0]); // Set the first date as active if not already set
      }
    }
  }, [lastAsnOrAgsnGeneratedDate, setActiveDate, activeDate]);

  useEffect(() => {
    if (!asnData.loading && asnsSelectedForLineTransfer && asnsSelectedForLineTransfer.length > 0) {
      getIndexOfLastAsnNotInPlannedState(asnData.data);
      updateLineSequenceWithGivenAsns(asnData.data); 
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asnData.loading, asnsSelectedForLineTransfer]);

  useEffect(() => {
    if (asnsSelectedForLineTransfer && asnsSelectedForLineTransfer.length > 0) {
      setIsLoadingAsnToLineOn(false);
    }
  }, [asnsSelectedForLineTransfer]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="heijunka-sequence-modal"
      aria-describedby="modal to show heijunka sequence"
    >
      <div className={styles.container}>
        {isLoadingAsnToLineOn || asnData.loading ? <LinearProgress /> : renderTableView()}
      </div>
    </Modal>
  );
};

LineTransferSelectorDisplay.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  updateRows: PropTypes.func.isRequired,
  asnsSelectedForLineTransfer: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ),
  activeDate: PropTypes.objectOf(moment),
  setActiveDate: PropTypes.func.isRequired,
  asnData: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
    data: PropTypes.array.isRequired,
    error: PropTypes.string,
  }),
  asnSelectorheaderText: PropTypes.string.isRequired,
  setAsnData: PropTypes.func.isRequired,
  lastAsnOrAgsnGeneratedDate: PropTypes.objectOf(moment),
  handleLineOnSubmit: PropTypes.func.isRequired,
  fixedColumns: PropTypes.array.isRequired,
  selectedLine: PropTypes.string.isRequired,
};

export default LineTransferSelectorDisplay;